import {ErrorStatus} from 'platform/components';
import {Grid, Show, ValueByDevice} from 'platform/foundation';

import {defaultTo, isEmpty, isNil, isNotEmpty, isNotNil} from 'ramda';

import {metadaWorkshopServiceOrderApi, useGetWorkshopOverviewQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {useDashboardWidgetSettings} from '../../hooks/useDashboardWidgetSettings';
import {DashboardTileSize} from '../../types/DashboardTileSize';
import {WorkshopOverviewCard} from './components/WorkshopOverviewCard';
import {WorkshopOverviewCompact} from './components/WorkshopOverviewCompact';
import {WorkshopOverviewFilterOptions} from './components/WorkshopOverviewFilterOptions';
import {WorkshopOverviewSection} from './components/WorkshopOverviewSection';
import {WORKSHOP_OVERVIEW_DENSITY_BY_SIZE} from './constants/workshopOverviewDensityBySize';
import {WORKSHOP_OVERVIEW_TILES} from './constants/workshopOverviewTiles';
import {useWorkshopDashboardFilters} from './hooks/useWorkshopDashboardFilters';
import {useWorkshopTileRedirect} from './hooks/useWorkshopTileRedirect';
import {WorkshopOverviewInteraction} from './types/WorkshopOverviewInteraction';
import {
  WorkshopOverviewSectionState,
  WorkshopOverviewTileState,
  WorkshopOverviewTileConfig,
  WorkshopSection,
} from './types/WorkshopOverviewTileConfig';
import {getWorkshopTileFilters} from './utils/getWorkshopTileFilters';
import {mapWorkshopOverviewCounts} from './utils/mapWorkshopOverviewCounts';

const defaultToFalse = defaultTo(false);
const SECTION_ORDER: WorkshopSection[] = ['critical', 'warning', 'today'];

interface WorkshopOverviewProps extends TestIdProps {
  size: DashboardTileSize;
}

const getSectionColumns = (sectionCount: number): ValueByDevice<number> => [
  1,
  1,
  sectionCount,
  sectionCount,
];

export function WorkshopOverview(props: WorkshopOverviewProps) {
  const settings = useDashboardWidgetSettings();
  const dashboardFilters = useWorkshopDashboardFilters();
  // Read existing enum data without fetching while the filter panel is collapsed.
  const {data: orderTypes} =
    metadaWorkshopServiceOrderApi.endpoints.getServiceOrderTypes.useQueryState(undefined);
  const selectedOrderTypes = dashboardFilters.filters.serviceOrderTypeId.flatMap((id) => {
    const type =
      orderTypes?.find((candidate) => candidate?.id === id) ??
      dashboardFilters.orderTypes.find((candidate) => candidate.id === id);
    return isNotNil(type?.code) ? [{id, code: type.code}] : [];
  });
  const hasResolvedOrderTypes =
    isNotEmpty(selectedOrderTypes) &&
    selectedOrderTypes.length === dashboardFilters.filters.serviceOrderTypeId.length;

  const enabledTiles = WORKSHOP_OVERVIEW_TILES.filter((tile) =>
    defaultToFalse(settings?.[tile.settingsKey])
  );

  const overview = useGetWorkshopOverviewQuery(dashboardFilters.filters, {
    skip: enabledTiles.length === 0,
  });
  const {prefetchTileQuery, openTileGrid, pendingTileId} = useWorkshopTileRedirect();

  const isLoading = overview.isFetching || !overview.isSuccess;
  const counts = mapWorkshopOverviewCounts(isLoading ? undefined : overview.currentData);

  const tileStates: WorkshopOverviewTileState[] = enabledTiles
    .map((tile) => ({tile, count: counts[tile.id]}))
    .filter((tileState) => isLoading || isNotNil(tileState.count));

  const sections: WorkshopOverviewSectionState[] = SECTION_ORDER.map((section) => ({
    section,
    tiles: tileStates.filter((tileState) => tileState.tile.section === section),
  })).filter((sectionState) => isNotEmpty(sectionState.tiles));

  if (isEmpty(enabledTiles)) {
    return null;
  }

  const getFilteredTile = (tile: WorkshopOverviewTileConfig) => {
    const filters = getWorkshopTileFilters(
      tile,
      dashboardFilters.filters,
      selectedOrderTypes.map((type) => type.code)
    );
    return isNotNil(filters) ? {...tile, filters} : null;
  };

  const interaction: WorkshopOverviewInteraction = {
    isLoading,
    pendingTileId,
    isTileDisabled: (tile) => isLoading || isNil(getFilteredTile(tile)),
    getTileDisabledReason: (tile) =>
      !isLoading && hasResolvedOrderTypes && isNil(getFilteredTile(tile))
        ? i18n.t('page.homepage.workshopOverview.warrantyTypeNotSelected')
        : undefined,
    onOpenTile: (tile) => {
      const filteredTile = getFilteredTile(tile);
      if (!isLoading && isNotNil(filteredTile)) {
        if (isNotEmpty(selectedOrderTypes)) {
          dashboardFilters.onFilterChange(
            'serviceOrderTypeId',
            dashboardFilters.filters.serviceOrderTypeId,
            selectedOrderTypes
          );
        }
        openTileGrid(filteredTile);
      }
    },
    onPrefetchTile: (tile) => {
      const filteredTile = getFilteredTile(tile);
      if (!isLoading && isNotNil(filteredTile)) {
        prefetchTileQuery(filteredTile);
      }
    },
  };

  return (
    <WorkshopOverviewCard
      filters={dashboardFilters.filters}
      renderFilters={() => <WorkshopOverviewFilterOptions {...dashboardFilters} />}
      data-testid={props['data-testid']}
    >
      <Show when={overview.isError}>
        <ErrorStatus action={{title: i18n.t('general.actions.retry'), onClick: overview.refetch}} />
      </Show>
      <Show when={!overview.isError}>
        {WORKSHOP_OVERVIEW_DENSITY_BY_SIZE[props.size] === 'compact' ? (
          <WorkshopOverviewCompact
            sections={sections}
            interaction={interaction}
            data-testid={props['data-testid']}
          />
        ) : (
          <Grid columns={getSectionColumns(sections.length)} spacing={4}>
            {sections.map((sectionState) => (
              <WorkshopOverviewSection
                key={sectionState.section}
                section={sectionState.section}
                tiles={sectionState.tiles}
                interaction={interaction}
                data-testid={suffixTestId(`workshopOverview-${sectionState.section}`, props)}
              />
            ))}
          </Grid>
        )}
      </Show>
    </WorkshopOverviewCard>
  );
}
